import React from "react";
import { V3Icon } from "@components/v3/primitives";
import type { OnboardingLayoutProps } from "./index";
import type { StepReassuranceCopy } from "./stepChrome";
import {
  Actions,
  BackAction,
  PrimaryAction,
  Reassurance,
  Spinner,
  StepBody,
  StepFooter,
  StepForm,
  StepHeader,
  StepHeading,
  StepOptional,
  StepSubheading,
} from "./StepLayout.styled";

interface StepReassuranceProps {
  reassurance: StepReassuranceCopy;
}

export const StepReassurance = ({
  reassurance,
}: StepReassuranceProps): JSX.Element => (
  <Reassurance>
    <V3Icon name={reassurance.icon} size={16} />
    {reassurance.text}
  </Reassurance>
);

export interface StepActionRowProps {
  nextLabel?: string;
  backLabel?: string;
  hideBack?: boolean;
  hideNext?: boolean;
  disableBack?: boolean;
  disableNext?: boolean;
  isSubmitting?: boolean;
  onBack?: () => void;
  /** Omitted when the primary action submits the surrounding form. */
  onNext?: () => void;
  /** Keeps Back from submitting the surrounding form. */
  preventBackSubmit?: boolean;
  nextTestId?: string;
  backTestId?: string;
}

/** Back on the left, the primary action on the right, as one action bar. */
export const StepActionRow = ({
  nextLabel = "Continue",
  backLabel = "Back",
  hideBack,
  hideNext,
  disableBack,
  disableNext,
  isSubmitting,
  onBack,
  onNext,
  preventBackSubmit = true,
  nextTestId = "submit-button",
  backTestId,
}: StepActionRowProps): JSX.Element => (
  <Actions>
    {hideBack ? null : (
      <BackAction
        type={preventBackSubmit ? "button" : "submit"}
        disabled={disableBack}
        onClick={onBack}
        data-testid={backTestId}
      >
        <V3Icon name="arrow-left" size={20} />
        {backLabel}
      </BackAction>
    )}
    {hideNext ? null : (
      <PrimaryAction
        type={onNext ? "button" : "submit"}
        onClick={onNext}
        data-testid={nextTestId}
        disabled={isSubmitting || disableNext}
        aria-busy={isSubmitting || undefined}
      >
        {isSubmitting ? <Spinner aria-hidden="true" /> : null}
        {nextLabel}
      </PrimaryAction>
    )}
  </Actions>
);

const NON_FIELD_INPUTS = ["button", "submit", "reset", "image", "file"];

/** Enter in a field submits the step, as a desktop form would; open lists, buttons and uploads keep their own Enter. */
export const submitOnEnter = (
  event: React.KeyboardEvent<HTMLFormElement>
): void => {
  const target = event.target as HTMLElement;
  const submitter = event.currentTarget.querySelector<HTMLButtonElement>(
    'button[type="submit"]:not(:disabled)'
  );
  if (
    event.key !== "Enter" ||
    event.defaultPrevented ||
    event.nativeEvent.isComposing ||
    event.shiftKey ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    !(target instanceof HTMLInputElement) ||
    NON_FIELD_INPUTS.includes(target.type) ||
    target.getAttribute("aria-expanded") === "true" ||
    !submitter
  ) {
    return;
  }
  event.preventDefault();
  event.currentTarget.requestSubmit(submitter);
};

interface StepLayoutProps extends OnboardingLayoutProps {
  children?: React.ReactNode;
  reassurance?: StepReassuranceCopy;
}

/** OnboardingLayout inside a stepped flow: one question per screen, the actions pinned under it. */
export const StepLayout = ({
  children,
  heading,
  subheading,
  helperText,
  optionalText,
  disableBack,
  disableNext,
  hideBackButton,
  hideNextButton,
  backBtnText,
  nextBtnText,
  onSubmit,
  onClickBack,
  isSubmitting,
  preventBackSubmit,
  headingId,
  nextTestId = "submit-button",
  backTestId,
  reassurance,
}: StepLayoutProps): JSX.Element => {
  return (
    <StepForm onSubmit={onSubmit} onKeyDown={submitOnEnter}>
      {heading || subheading || helperText ? (
        <StepHeader>
          {heading ? (
            <StepHeading id={headingId} tabIndex={headingId ? -1 : undefined}>
              {heading}
              {optionalText ? (
                <StepOptional>{optionalText}</StepOptional>
              ) : null}
            </StepHeading>
          ) : null}
          {subheading ? <StepSubheading>{subheading}</StepSubheading> : null}
          {helperText ? <StepSubheading>{helperText}</StepSubheading> : null}
        </StepHeader>
      ) : null}
      {children ? <StepBody>{children}</StepBody> : null}
      <StepFooter data-step-footer="">
        <StepActionRow
          nextLabel={nextBtnText || undefined}
          backLabel={backBtnText || undefined}
          hideBack={hideBackButton}
          hideNext={hideNextButton}
          disableBack={disableBack}
          disableNext={disableNext}
          isSubmitting={isSubmitting}
          onBack={onClickBack}
          preventBackSubmit={!!preventBackSubmit}
          nextTestId={nextTestId}
          backTestId={backTestId}
        />
        {reassurance ? <StepReassurance reassurance={reassurance} /> : null}
      </StepFooter>
    </StepForm>
  );
};
